<template>
    <b-v-form-btn-label-control
        ref="control"
        :placeholder="placeholder"
        :class="{
            primary: variant === 'primary',
            'lx-responsive-label-control': !!responsiveness,
        }"
        :value="labelControlValue"
        :select-size="selectSize"
        :state="state"
        @show="onControlShow"
        @hide="onControlHide"
        @shown="onControlShown"
        @hidden="onControlHidden"
    >
        <template v-slot:button-content>
            <font-awesome-icon
                v-if="icon"
                :icon="icon"
                class="lx-label-control-icon"
                size="lg"
            ></font-awesome-icon>
        </template>
        <template v-slot>
            <search-multiselect-card
                :key="cardID"
                :value="value"
                :options="options"
                :is-searchable="isSearchable"
                :is-controls="isControls"
                :applyDefaultSort="applyDefaultSort"
                :hideSelectAll="hideSelectAll"
                @input="onValueChange"
            />
        </template>
    </b-v-form-btn-label-control>
</template>

<script>
import { BVFormBtnLabelControl } from 'bootstrap-vue/src/components/form-btn-label-control/bv-form-btn-label-control';
import FormInput from '../form-input/form-input.vue';
import SearchMultiselectCard from '../search-multiselect-card/search-multiselect-card.vue';

export default {
    name: 'SearchMultiselect',
    components: {
        BVFormBtnLabelControl,
        FormInput,
        SearchMultiselectCard,
    },
    props: {
        value: {
            type: Array,
            default: () => [],
        },
        options: {
            type: Array,
            default: () => [],
        },
        selectSize: {
            type: Number,
            default: 8,
        },
        isSearchable: {
            type: Boolean,
            default: true,
        },
        isControls: {
            type: Boolean,
            default: true,
        },
        placeholder: {
            type: String,
            default: '',
        },
        controlValue: {
            type: String,
            default: '',
        },
        variant: {
            type: String,
            default: '',
        },
        state: {
            type: Boolean,
            default: null,
        },
        icon: [Object, Array, String],
        itemLabel: {
            type: String,
            default: 'item',
        },
        responsiveness: {
            type: Boolean,
            default: false,
        },
        applyDefaultSort: {
            type: Boolean,
            default: true,
        },
        hideSelectAll: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            cardID: 0,
        };
    },
    computed: {
        labelControlValue() {
            let controlValue = this.controlValue;

            if (this.value.length === 1) {
                const item = this.options.find(
                    (option) => option.value === this.value[0]
                );
                if (item) {
                    controlValue = `${item.text}`;
                }
            } else if (this.value.length >= 2) {
                controlValue = `${this.value.length} ${this.itemLabel}s selected`;
            }
            return controlValue;
        },
    },
    methods: {
        onControlShow(e) {
            this.$emit('show', e);
        },
        onControlHide(e) {
            this.$emit('hide', e);
        },
        onControlShown() {
            this.$emit('shown');
        },
        onControlHidden() {
            this.cardID += 1;
            this.$emit('hidden');
        },
        onValueChange(value) {
            this.$emit('input', value);
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

$primary-color: #0d47a1;
$font-color: #fff;

.primary {
    color: $font-color !important;
    background-color: $primary-color !important;
    border-color: $primary-color !important;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.15),
        0 1px 1px rgba(0, 0, 0, 0.075) !important;
    transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
        border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;

    &:hover {
        background-color: #0a377e !important;
        border-color: #093272 !important;
    }

    &.focus {
        box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 0 0.2rem rgba(13, 71, 161, 0.25) !important
    }

    > :deep(label) {
        color: $font-color !important;
    }
}

.b-form-btn-label-control.form-control :deep(button) {
    padding: 5px !important;
}

.b-form-btn-label-control.form-control.is-invalid :deep(label) {
    padding-right: 5px !important;
}

.b-form-btn-label-control.form-control :deep(.lx-label-control-icon) {
    color: $font-color;
    margin-left: 0.375rem;
}

.b-form-btn-label-control.form-control :deep(label) {
    padding-left: 0 !important;
    user-select: none !important;
    box-shadow: none !important;
    display: flex !important;
    justify-content: space-between;
    align-items: center;
}

.b-form-btn-label-control.form-control :deep(label:after) {
    display: inline-block;
    margin-left: 0.255em;
    vertical-align: 0.255em;
    content: '';
    border-top: 0.3em solid;
    border-right: 0.3em solid transparent;
    border-bottom: 0;
    border-left: 0.3em solid transparent;
}

@include media-breakpoint-down(md) {
    .lx-responsive-label-control.form-control {
        height: calc(1.5em + 0.75rem + 2px) !important;
    }

    .lx-responsive-label-control.form-control :deep(label) {
        display: none !important;
    }

    .lx-responsive-label-control.form-control :deep(button:after) {
        color: $font-color;
        display: inline-block;
        margin-left: 0.375rem;
        margin-right: 0.375rem;
        vertical-align: 0.255em;
        content: '';
        border-top: 0.3em solid;
        border-right: 0.3em solid transparent;
        border-bottom: 0;
        border-left: 0.3em solid transparent;
    }
}

@media (max-width: 2000px) {
    :deep(.form-search-multiselect__group-item-text-wrapper) {
        width: 350px;
    }
}

@media (max-width: 600px) {
    :deep(.form-search-multiselect__group-item-text-wrapper) {
        width: 300px;
    }
}

@media (max-width: 400px) {
    :deep(.form-search-multiselect__group-item-text-wrapper) {
        width: 250px;
    }
}

@media (max-width: 352px) {
    :deep(.form-search-multiselect__group-item-text-wrapper) {
        width: 200px;
    }
}
</style>